// Audit Log Hook

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { AuditLogEntry, AuditAction } from '@/lib/security/types';

interface AuditLogFilters {
  clientId?: string;
  userId?: string;
  action?: AuditAction;
  resourceType?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
}

interface UseAuditLogOptions {
  filters?: AuditLogFilters;
  limit?: number;
  enabled?: boolean;
}

export function useAuditLog(options: UseAuditLogOptions = {}) {
  const [page, setPage] = useState(0);
  const limit = options.limit || 50;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit-logs', options.filters, page, limit],
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' });

      const filters = options.filters || {};
      
      if (filters.clientId) {
        query = query.eq('client_id', filters.clientId);
      }
      if (filters.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.resourceType) {
        query = query.eq('resource_type', filters.resourceType);
      }
      if (filters.resourceId) {
        query = query.eq('resource_id', filters.resourceId);
      }
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      query = query
        .order('created_at', { ascending: false })
        .range(page * limit, (page + 1) * limit - 1);

      const { data, count, error } = await query;

      if (error) throw error;

      return {
        logs: data as AuditLogEntry[],
        total: count || 0,
        page,
        pageCount: Math.ceil((count || 0) / limit),
      };
    },
    enabled: options.enabled !== false,
  });

  const nextPage = useCallback(() => {
    if (data && page < data.pageCount - 1) {
      setPage(p => p + 1);
    }
  }, [data, page]);

  const prevPage = useCallback(() => {
    if (page > 0) {
      setPage(p => p - 1);
    }
  }, [page]);

  const goToPage = useCallback((pageNum: number) => {
    if (pageNum >= 0 && (!data || pageNum < data.pageCount)) {
      setPage(pageNum);
    }
  }, [data]);

  return {
    logs: data?.logs || [],
    total: data?.total || 0,
    page: data?.page || 0,
    pageCount: data?.pageCount || 0,
    isLoading,
    error,
    refetch,
    nextPage,
    prevPage,
    goToPage,
  };
}

/**
 * Hook for fetching audit logs for a specific resource
 */
export function useResourceAuditLog(resourceType: string, resourceId: string) {
  return useAuditLog({
    filters: { resourceType, resourceId },
    enabled: !!resourceId,
  });
}

/**
 * Hook for fetching audit logs for a specific case
 */
export function useCaseAuditLog(caseId: string) {
  return useAuditLog({
    filters: { resourceType: 'authorization_request', resourceId: caseId },
    enabled: !!caseId,
  });
}
